import CancelIcon from "@mui/icons-material/Cancel";
import { Checkbox, Grid, IconButton, Typography } from "@mui/material";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import usePaginationWithLocalSearchParamsAndOrdering from "../../../hooks/usePaginationWithLocalSearchParamsAndOrdering";
import { useCustomers, useCustomersByIds } from "../../../services/customersService";
import { FilterTableWrapper } from "../../table/BetterFilterTable/BetterFilterTable";

export const CustomerSelectionFormControl = (props) => {
    const { control, fieldName, readOnly = false } = props;

    const maxCustomerSelection = Number.parseInt(window._env_?.MAX_CUSTOMER_SELECTION || "10", 10);

    const { setValue } = useFormContext();
    const selectedCustomersIds = useWatch({
        control,
        name: fieldName,
    });

    const [selectedCustomersCache, setSelectedCustomersCache] = useState(new Map());

    const { data: selectedCustomersData, isLoading } = useCustomersByIds(selectedCustomersIds || [], {
        enabled: (selectedCustomersIds?.length ?? 0) > 0,
    });

    useEffect(() => {
        if (selectedCustomersData) {
            setSelectedCustomersCache((prev) => {
                const newCache = new Map(prev);
                selectedCustomersData.forEach((customer) => {
                    newCache.set(customer.id, customer);
                });
                return newCache;
            });
        }
    }, [selectedCustomersData]);

    const toggleSelect = useCallback(
        (customerId, customerData) => {
            const alreadySelected = selectedCustomersIds?.includes(customerId);
            if (alreadySelected) {
                const updatedCustomersIds = (selectedCustomersIds || []).filter(
                    (id) => id !== customerId,
                );
                setValue(fieldName, updatedCustomersIds);

                setSelectedCustomersCache((prev) => {
                    const newCache = new Map(prev);
                    newCache.delete(customerId);
                    return newCache;
                });
            } else {
                if ((selectedCustomersIds || []).length >= maxCustomerSelection) {
                    return;
                }

                const updatedCustomersIds = [...(selectedCustomersIds || []), customerId];
                setValue(fieldName, updatedCustomersIds);

                if (customerData) {
                    setSelectedCustomersCache((prev) => {
                        const newCache = new Map(prev);
                        newCache.set(customerId, customerData);
                        return newCache;
                    });
                }
            }
        },
        [selectedCustomersIds, setValue, fieldName, maxCustomerSelection],
    );

    const pagination = usePaginationWithLocalSearchParamsAndOrdering();

    const { data: customersData } = useCustomers(pagination.searchParams);

    const customers = useMemo(
        () =>
            customersData?.results.map((v) => ({
                id: v.id,
                fname: v.person?.name,
                lname: v.person?.surname,
                company_full_name: v.company?.name,
                email: v.company ? v.company.email?.email_address : v.person?.email?.email_address,
                phone: v.company ? v.company.phone?.phone_number : v.person?.phone?.phone_number,
                status: v.status,
                originalData: v,
            })) || [],
        [customersData?.results],
    );

    useEffect(() => {
        if (customers && selectedCustomersIds) {
            setSelectedCustomersCache((prev) => {
                const newCache = new Map(prev);
                customers.forEach((customer) => {
                    if (selectedCustomersIds.includes(customer.id)) {
                        newCache.set(customer.id, customer.originalData);
                    }
                });
                return newCache;
            });
        }
    }, [customers, selectedCustomersIds]);

    const selectedCustomersFromCache = useMemo(() => {
        if (!selectedCustomersIds?.length) {
            return [];
        }

        return selectedCustomersIds
            .map((id) => selectedCustomersCache.get(id))
            .filter((v) => v !== undefined)
            .map((v) => ({
                id: v.id,
                fname: v.person?.name,
                lname: v.person?.surname,
                company_full_name: v.company?.name,
                email: v.company ? v.company.email?.email_address : v.person?.email?.email_address,
                phone: v.company ? v.company.phone?.phone_number : v.person?.phone?.phone_number,
                status: v.status,
            }));
    }, [selectedCustomersIds, selectedCustomersCache]);

    const customerSelectionTable = () => (
        <FilterTableWrapper
            filtersOpts={{
                showCleanFilterIcon: true,
            }}
            onSearchFiltersChange={pagination.handleChangeSearchParams}
            localPagination={pagination}
            data={customers}
            countRecords={customersData?.count}
            fieldsConfig={[
                {
                    getValueCallback: (row) => (
                        <Checkbox
                            onChange={() => toggleSelect(row.id, row.originalData)}
                            checked={selectedCustomersIds?.includes(row.id) || false}
                            disabled={
                                readOnly ||
                                (!selectedCustomersIds?.includes(row.id) &&
                                    (selectedCustomersIds || []).length >= maxCustomerSelection)
                            }
                        />
                    ),
                },
                {
                    name: "fname",
                    getValueCallback: (row) => row.fname || "-",
                    filterType: "text",
                    label: t("CustomerSelectionFormControl.table.clientName"),
                },
                {
                    name: "lname",
                    getValueCallback: (row) => row.lname || "-",
                    filterType: "text",
                    label: t("CustomerSelectionFormControl.table.clientSurname"),
                },
                {
                    name: "company_full_name",
                    getValueCallback: (row) => row.company_full_name || "-",
                    label: t("CustomerSelectionFormControl.table.companyName"),
                    filterType: "text",
                },
                {
                    name: "email",
                    getValueCallback: (row) => row.email || "-",
                    label: t("CustomerSelectionFormControl.table.email"),
                    filterType: "text",
                },
                {
                    name: "phone",
                    getValueCallback: (row) => row.phone || "-",
                    label: t("CustomerSelectionFormControl.table.phone"),
                    filterType: "text",
                },
            ]}
            isLoading={isLoading}
        />
    );

    const selectedCustomersTable = () => (
        <FilterTableWrapper
            filtersOpts={{
                hideFiltersIconCol: true,
            }}
            data={selectedCustomersFromCache}
            fieldsConfig={[
                {
                    getValueCallback: (row) => (
                        <IconButton onClick={() => toggleSelect(row.id)} disabled={readOnly}>
                            <CancelIcon color={"error"} />
                        </IconButton>
                    ),
                },
                {
                    name: "fname",
                    getValueCallback: (row) => row.fname || "-",
                    filterType: "text",
                    label: t("CustomerSelectionFormControl.table.clientName"),
                },
                {
                    name: "lname",
                    getValueCallback: (row) => row.lname || "-",
                    filterType: "text",
                    label: t("CustomerSelectionFormControl.table.clientSurname"),
                },
                {
                    name: "company_full_name",
                    getValueCallback: (row) => row.company_full_name || "-",
                    label: t("CustomerSelectionFormControl.table.companyName"),
                    filterType: "text",
                },
                {
                    name: "email",
                    getValueCallback: (row) => row.email || "-",
                    label: t("CustomerSelectionFormControl.table.email"),
                    filterType: "text",
                },
                {
                    name: "phone",
                    getValueCallback: (row) => row.phone || "-",
                    label: t("CustomerSelectionFormControl.table.phone"),
                    filterType: "text",
                },
            ]}
        />
    );

    return (
        <Grid container sx={{ width: "100%" }} spacing={2}>
            <Grid item sx={{ width: "100%" }}>
                {customerSelectionTable()}
            </Grid>
            <Grid item sx={{ width: "100%" }}>
                <Grid>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        {t("CustomerSelectionFormControl.selectedCustomers")}
                    </Typography>
                </Grid>
                <Grid sx={{ width: "100%" }}>{selectedCustomersTable()}</Grid>
            </Grid>
        </Grid>
    );
};
